import { useState, useEffect, useRef } from 'react';
import { usePWAInstall } from './hooks/usePWAInstall';
import { usePWAUpdate } from './hooks/usePWAUpdate';
import { useTheme } from './hooks/useTheme';
import { useTtsAutoAdvance } from './hooks/useTtsAutoAdvance';
import { useWakeLock } from './hooks/useWakeLock';
import { saveLastRead, loadLastRead, triggerHaptic, clearLastRead } from './utils/smartReader';
import { speakVerses, stopSpeaking } from './utils/tts';
import { clearTtsProgress, loadTtsProgress } from './utils/ttsProgress';
import { logTtsEvent } from './utils/ttsController';
import { CorpusService } from './services/CorpusRegistry';
import { LibraryDB } from './services/LibraryDB';
import { LibraryView } from './components/LibraryView';
import { ReaderFlipView } from './components/ReaderFlipView';
import { DiscoverView } from './components/DiscoverView';
import { SettingsModal } from './components/SettingsModal';
import { ChatDrawer } from './components/ChatDrawer';
import { Toast } from './components/Toast';
import { HelpView } from './components/HelpView';
import './App.css';

import { AudioPlayerOverlay } from './components/AudioPlayerOverlay';
import { AdminFactory } from './components/AdminFactory';
import { ImportModal } from './components/ImportModal';
import { DeveloperConsole } from './components/admin/DeveloperConsole';
import { ErrorBoundary } from './components/ErrorBoundary';
import { getApiUrl, probeApiRoute } from './utils/urlHelper';
import { validateBookText, isCorruptLocalData } from './utils/textValidator';
import { LocalImporter } from './services/LocalImporter';
import { fetchArrayBufferViaWorker } from './utils/fetchProxy';

const App = () => {
    // Hooks
    const pwa = usePWAInstall();
    const { needRefresh, updateServiceWorker } = usePWAUpdate();
    const [showChat, setShowChat] = useState(false);
    const [showSettings, setShowSettings] = useState<string | null>(null);

    // Commercial / Library Audio
    const [audioManifest, setAudioManifest] = useState<any[]>([]);

    // Book Club Errors
    const [bookClubError, setBookClubError] = useState<string | null>(null);

    // Initial Load & Entitlement
    useEffect(() => {
        const initApp = async () => {
            await probeApiRoute();

            // 1. Load Audio Manifest (Offline-First strategy)
            try {
                const items = await LibraryDB.getAudioManifest();
                if (items && items.length > 0) {
                    console.log(`[App] Loaded ${items.length} items from offline manifest cache`);
                    setAudioManifest(items);
                }
            } catch (e) {
                console.warn("Failed to load offline manifest", e);
            }

            // 2. Fetch Fresh Manifest (Stale-While-Revalidate)
            try {
                const r = await fetch(getApiUrl('/library/manifest'));
                const d = await r.json();
                let items: any[] = [];
                if (Array.isArray(d)) items = d;
                else if (d.ok && d.items) items = d.items;

                if (items.length > 0) {
                    setAudioManifest(items);
                    await LibraryDB.saveAudioManifest(items);
                }
            } catch (e) {
                console.error("Failed to fetch fresh manifest", e);
            }

            // Entitlement (Check once on load)
            try {
                const r = await fetch(getApiUrl('/entitlement'));
                const d = await r.json();
                if (typeof d.isPro === 'boolean') setIsPro(d.isPro);
                if (typeof d.isBookClubMember === 'boolean') setIsBookClubMember(d.isBookClubMember);
                else if (typeof d.isPro === 'boolean') setIsBookClubMember(d.isPro);
            } catch (e) {
                console.error("Entitlement check failed", e);
            }
        };
        initApp();
    }, []);

    const [audioOverlay, setAudioOverlay] = useState<{ url: string, title: string, coverUrl?: string, startTime?: number, bookId?: string } | null>(null);
    const [showBookMenu, setShowBookMenu] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);

    // Book Club Membership State (P0-C)
    const [isBookClubMember, setIsBookClubMember] = useState(false); // Default false for non-members
    const [isPro, setIsPro] = useState(false); // Default false for non-pro
    const [debugMemberOverride, setDebugMemberOverride] = useState(false); // Debug toggle

    // Effective Member Status
    const isMember = isBookClubMember || debugMemberOverride;

    // Refs for Play Button Long Press
    const longPressTimer = useRef<any>(null); // Type 'any' to handle NodeJS vs Browser timeout types if needed
    const isLongPress = useRef(false);

    // Refresh Entitlement when Settings close (in case PIN changed)
    useEffect(() => {
        if (!showSettings) {
            fetch(getApiUrl('/entitlement')).then(r => r.json()).then(d => {
                if (typeof d.isPro === 'boolean') setIsPro(d.isPro);
                if (typeof d.isBookClubMember === 'boolean') setIsBookClubMember(d.isBookClubMember);
                else if (typeof d.isPro === 'boolean') setIsBookClubMember(d.isPro);
            }).catch(() => { });
        }
    }, [showSettings]);



    // Common State
    const { theme, setTheme } = useTheme();
    const [isDesktop, setIsDesktop] = useState(window.innerWidth > 900);

    // Router State: Initialize based on current path
    const [view, setView] = useState<'library' | 'discover' | 'reader' | 'settings' | 'help' | 'admin' | 'admin-console'>(() => {
        const path = window.location.pathname.replace(/\/$/, '');
        if (path === '/admin/factory') return 'admin';
        if (path === '/admin/console') return 'admin-console';
        return 'library';
    });

    const wakeLock = useWakeLock();

    // Removed duplicated useEffect router logic


    // Corpus
    const [activeCorpusId, setActiveCorpusId] = useState(() => CorpusService.getActive().id);
    const activeCorpus = CorpusService.getActive();

    // Chat State (Lifted)
    const [chatMessages, setChatMessages] = useState<any[]>([]);

    // Reading State
    const [currentBookId, setCurrentBookId] = useState<string>('');
    const [currentChapter, setCurrentChapter] = useState<number>(1);

    // Settings
    const [smartReader, setSmartReader] = useState(() => localStorage.getItem('bible_smart_reader') !== 'false');
    useEffect(() => { localStorage.setItem('bible_smart_reader', String(smartReader)); }, [smartReader]);

    const [haptics, setHaptics] = useState(() => localStorage.getItem('bible_haptics') !== 'false');
    useEffect(() => { localStorage.setItem('bible_haptics', String(haptics)); }, [haptics]);

    const [readerMode, setReaderMode] = useState<'scroll' | 'flip'>(() => (localStorage.getItem('bible_reader_mode') as 'scroll' | 'flip') || 'flip');
    const [toastMsg, setToastMsg] = useState<string | null>(null);

    // Content
    const [books, setBooks] = useState(() => activeCorpus.getSections());
    const [verseData, setVerseData] = useState<Record<string, string> | null>(null);
    const [pages, setPages] = useState<string[][]>([]);
    const [pageIndex, setPageIndex] = useState(0);
    const [isPaginating, setIsPaginating] = useState(false);

    // Narrated Tip (One-time)
    useEffect(() => {
        if (!audioManifest.length || view !== 'reader') return;

        const book = books.find(b => b.id === currentBookId);
        let bookAudio = audioManifest.find(m => m.id === currentBookId);
        if (!bookAudio && book?.title?.toLowerCase().includes('siddhartha')) {
            bookAudio = audioManifest.find(m => m.id === 'siddhartha');
        }

    }, [currentBookId, audioManifest, view, books]);

    // Restore State Logic
    const [pendingRestore, setPendingRestore] = useState<{ page: number } | null>(null);

    // TTS State & Controller
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);

    // Check for resume capability
    const [resumeHint, setResumeHint] = useState<string | null>(null);
    useEffect(() => {
        if (!currentBookId) return;
        const saved = loadTtsProgress(activeCorpusId, currentBookId);
        if (saved && saved.chapter === currentChapter && saved.pageIndex === pageIndex) {
            setResumeHint(`Resume: ${saved.snippet}...`);
        } else {
            setResumeHint(null);
        }
    }, [currentBookId, currentChapter, pageIndex, activeCorpusId, isSpeaking]);

    // Refs for TTS callbacks to access fresh state
    const readerStateRef = useRef({ pageIndex, pages, currentChapter, books, currentBookId });
    const lastManualNavAtRef = useRef<number>(0);

    useEffect(() => {
        readerStateRef.current = { pageIndex, pages, currentChapter, books, currentBookId };

        // Safety Guard: If page changed manually and we are still speaking (race condition), force stop.
        // We use a timestamp window (500ms) to prevent sticky state from killing future play sessions.
        if (isSpeaking) {
            const timeSinceNav = Date.now() - lastManualNavAtRef.current;
            if (timeSinceNav < 500) {
                console.log("[App] Safety Guard: Stopping TTS after manual nav (race detected)");
                stopSpeaking('manual_nav_guard');
            }
        }
    }, [pageIndex, pages, currentChapter, books, currentBookId, isSpeaking]);

    // Helper for next chapter/page logic (used by TTS and UI)
    const advancePageOrChapter = (reason: 'auto' | 'manual'): boolean => {
        const { pageIndex, pages, currentChapter, books, currentBookId } = readerStateRef.current;

        if (reason === 'manual') {
            lastManualNavAtRef.current = Date.now();
        }

        logTtsEvent('PAGE_NAV', { reason, action: 'attempt_next', whilePlaying: isSpeaking });

        if (pageIndex < pages.length - 1) {
            setPageIndex(p => p + 1);
            return true;
        } else {
            // Next Chapter Logic
            const nextCh = currentChapter + 1;
            // Try to find current Book Meta
            const bookMeta = books.find(b => b.id === currentBookId);
            const maxCh = (bookMeta as any)?.chapterCount;

            if (maxCh && nextCh > maxCh) {
                return false; // End of book
            }
            // Start Loading Next Chapter
            // Clear current data to prevent stale reads during "auto" transitions
            setVerseData(null);
            setCurrentChapter(nextCh);
            return true;
        }
    };

    // UI State for Chapter Nav
    const [showChapterSheet, setShowChapterSheet] = useState(false);

    // Auto-Advance Hook
    const tts = useTtsAutoAdvance({
        isPlaying: isSpeaking,
        setIsPlaying: setIsSpeaking,
        pageIndex,
        pages,
        verseData,
        isPaginating,
        advancePageOrChapter,
        onToast: setToastMsg,
        setIsBuffering,
        bookId: currentBookId,
        chapter: currentChapter,
        corpusId: activeCorpusId
    });

    const handleTtsToggle = (text: string[]) => {
        if (isSpeaking) {
            tts.stop();
        } else {
            tts.play(text);
        }
    };

    // Resize Listener
    useEffect(() => {
        const handler = () => setIsDesktop(window.innerWidth > 900);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    // Global Developer Helpers
    useEffect(() => {
        (window as any).purgeLocalBook = async (bookId: string) => {
            console.log(`[App] Purging local book ${bookId}...`);
            try {
                await LibraryDB.deleteBook(bookId);
                console.log(`[App] Successfully purged book ${bookId} from local IndexedDB.`);
                return true;
            } catch (e) {
                console.error(`[App] Failed to purge book ${bookId}:`, e);
                return false;
            }
        };

        (window as any).purgeCorruptBooks = async (): Promise<number> => {
            console.log(`[App] Scanning for corrupted books...`);
            let purgedCount = 0;
            try {
                const books = await LibraryDB.getAllBooks();
                for (const book of books) {
                    let isCorrupt = false;
                    
                    if (book.textContent && isCorruptLocalData(book.textContent)) {
                        isCorrupt = true;
                    }
                    if (book.chunkedChapters && book.chunkedChapters.some(ch => isCorruptLocalData(ch))) {
                        isCorrupt = true;
                    }
                    const asset = await LibraryDB.getBookAsset(book.id);
                    if (asset && asset.text && isCorruptLocalData(asset.text)) {
                        isCorrupt = true;
                    }
                    if (book.textContent && book.textContent.startsWith('PK')) {
                        isCorrupt = true;
                    }
                    if (asset && asset.text && asset.text.startsWith('PK')) {
                        isCorrupt = true;
                    }

                    if (isCorrupt) {
                        console.warn(`[App] Purging corrupted content for book ${book.id}: ${book.title}`);
                        await LibraryDB.deleteBookAsset(book.id);
                        book.textContent = '';
                        book.chunkedChapters = [];
                        book.status = 'metadata-only';
                        await LibraryDB.addBook(book);
                        purgedCount++;
                        
                        const isNumeric = !isNaN(Number(book.id));
                        if (isNumeric) {
                            const corpus: any = CorpusService.get('public_domain_gutendex');
                            if (corpus && typeof corpus.downloadBook === 'function') {
                                try {
                                    console.log(`[App] Re-importing book ${book.id} via PublicDomainCorpus...`);
                                    await corpus.downloadBook({
                                        id: Number(book.id),
                                        title: book.title,
                                        authors: book.authors,
                                        languages: book.languages || ['en'],
                                        formats: book.formats || {}
                                    } as any);
                                } catch (downloadErr) {
                                    console.error(`[App] Recovery download failed for ${book.id}`, downloadErr);
                                }
                            }
                        }
                    }
                }
                console.log(`[App] Scanned books. Purged ${purgedCount} corrupted book(s).`);
                return purgedCount;
            } catch (e) {
                console.error(`[App] Failed to purge corrupt books:`, e);
                return -1;
            }
        };
    }, []);

    // Load Books on Corpus Change
    useEffect(() => {
        activeCorpus.load().then(() => {
            const secs = activeCorpus.getSections();
            setBooks(secs);
        });
    }, [activeCorpusId]);

    // Share Target Handler
    useEffect(() => {
        if (window.location.pathname === '/share') {
            // Basic extraction for now - just to acknowledge the share
            const params = new URLSearchParams(window.location.search);
            const title = params.get('title');
            const url = params.get('text') || params.get('url'); // Text often contains URL in Android share

            if (title || url) {
                setToastMsg(`Received Share: ${title || 'Content'}`);
                console.log('Share Target Data:', { title, url });
            }

            // Clear clean URL to avoid reload loop or stale state
            window.history.replaceState({}, '', '/');
        }
    }, []);

    // Book Selection & Resume Logic
    const handleSelectBook = (bookId: string) => {
        setCurrentBookId(bookId);
        setView('reader');

        // Auto-Resume if SmartReader is on (or default)
        if (smartReader) {
            const saved = loadLastRead(activeCorpusId, bookId);
            if (saved) {
                setCurrentChapter(saved.chapter);
                if (saved.pageIndex !== undefined) {
                    setPendingRestore({ page: saved.pageIndex });
                }
                setToastMsg(`Resumed at Ch ${saved.chapter}`);
            } else {
                setCurrentChapter(1);
                setPageIndex(0);
            }
        }
    };

    const handleJump = (bookId: string, chapter: number, verse?: number) => {
        setCurrentBookId(bookId);
        setCurrentChapter(chapter);
        setView('reader');
        setShowSettings(null);
        // Verse jump could be handled by auto-scrolling or highlighting later
    };

    const [isBookClubLoading, setIsBookClubLoading] = useState(false);

    // Load Chapter Content & restore page if needed
    useEffect(() => {
        if (!currentBookId || view !== 'reader') return;

        // Reset error state
        setBookClubError(null);

        // Check if this is a Book Club book
        const bookClubItem = audioManifest.find(m => m.id === currentBookId);

        console.log(`[Reader] Opening ${currentBookId}. Local checking... Remote manifest: ${!!bookClubItem}`);

        const verifyAndRecoverBook = async (bookId: string): Promise<boolean> => {
            const entry = await LibraryDB.getBook(bookId);
            if (!entry) return true;

            const isBookClub = entry.isBookClub || entry.source === 'book-club';
            
            // Check if status is ready and text is invalid
            let isInvalid = false;
            let currentText = '';

            // Get text from asset
            const asset = await LibraryDB.getBookAsset(bookId);
            if (asset && asset.text) {
                currentText = asset.text;
                if (isCorruptLocalData(asset.text)) {
                    isInvalid = true;
                }
            }

            if (entry.textContent && isCorruptLocalData(entry.textContent)) {
                isInvalid = true;
            }

            if (entry.chunkedChapters && entry.chunkedChapters.some(ch => isCorruptLocalData(ch))) {
                isInvalid = true;
            }

            // Also check for "PK" zombie book
            if (currentText.startsWith('PK') || (entry.textContent && entry.textContent.startsWith('PK'))) {
                isInvalid = true;
            }

            if (isInvalid) {
                console.warn(`[App] Invalid book asset/metadata text for ${bookId} (contains app shell HTML or ZIP). Recovering...`);
                
                // Purge local book assets/cache
                await LibraryDB.deleteBookAsset(bookId);
                
                // Keep metadata only
                entry.textContent = '';
                entry.chunkedChapters = [];
                entry.status = 'metadata-only';
                await LibraryDB.addBook(entry);
                
                if (isBookClub) {
                    return false; 
                } else {
                    // For Gutenberg, trigger recovery re-download
                    const corpus: any = CorpusService.get('public_domain_gutendex');
                    if (corpus && typeof corpus.downloadBook === 'function') {
                        try {
                            const gutendexBook = {
                                id: Number(bookId),
                                title: entry.title,
                                authors: entry.authors,
                                languages: entry.languages || ['en'],
                                formats: entry.formats || {}
                            } as any;
                            await corpus.downloadBook(gutendexBook);
                            await corpus.load();
                            return true;
                        } catch (downloadErr) {
                            console.error(`[App] Recovery download failed for ${bookId}`, downloadErr);
                            return false;
                        }
                    }
                    return false;
                }
            }
            return true;
        };

        const fetchBookClubText = async () => {
            console.log(`[Reader] Fetching text for ${currentBookId}...`);
            setIsBookClubLoading(true);
            setIsPaginating(true); // Show spinner

            try {
                // 1. Check Offline Cache First (LibraryDB)
                const cached = await LibraryDB.getBookAsset(currentBookId);
                if (cached && cached.text) {
                    if (isCorruptLocalData(cached.text)) {
                        console.warn(`[Reader] Cached asset for ${currentBookId} is corrupt. Purging and recovering...`);
                        await LibraryDB.deleteBookAsset(currentBookId);
                        const entry = await LibraryDB.getBook(currentBookId);
                        if (entry) {
                            entry.textContent = '';
                            entry.chunkedChapters = [];
                            entry.status = 'metadata-only';
                            await LibraryDB.addBook(entry);
                        }
                        
                        // Check if numeric/Gutendex
                        const isNumeric = !isNaN(Number(currentBookId));
                        if (isNumeric) {
                            const recovered = await tryDownloadPublicDomain();
                            if (recovered) {
                                await activeCorpus.load();
                                const data = activeCorpus.getChapter(currentBookId, currentChapter);
                                if (data && Object.keys(data).length > 0) {
                                    setVerseData(data);
                                    setPageIndex(0);
                                    setIsPaginating(true);
                                    return;
                                }
                            }
                        }
                        throw new Error("Cached book data was corrupt and has been purged. Please try downloading again.");
                    } else {
                        console.log("[Reader] Found text in offline cache");
                        processText(cached.text);
                        setIsBookClubLoading(false);
                        setIsPaginating(false);
                        return;
                    }
                }

                // 2. Fetch via getApiUrl
                // Numeric IDs must never call /api/library/text/:id
                const isNumeric = !isNaN(Number(currentBookId));
                if (isNumeric) {
                    console.warn(`[Reader] Numeric ID ${currentBookId} attempted fetch from /api/library/text. Routing to PublicDomainCorpus...`);
                    const recovered = await tryDownloadPublicDomain();
                    if (recovered) {
                        await activeCorpus.load();
                        const data = activeCorpus.getChapter(currentBookId, currentChapter);
                        if (data && Object.keys(data).length > 0) {
                            setVerseData(data);
                            setPageIndex(0);
                            setIsPaginating(true);
                            return;
                        }
                    }
                    throw new Error("Public Domain book download failed.");
                }

                const r = await fetch(getApiUrl(`/library/text/${currentBookId}`));
                if (!r.ok) {
                    // P0-F: Handle JSON errors kindly
                    const type = r.headers.get("Content-Type") || "";
                    if (type.includes("application/json")) {
                        const errJson = await r.json();
                        throw new Error(errJson.error || errJson.message || `Status ${r.status}`);
                    }
                    throw new Error(`Status ${r.status}`);
                }
                const text = await r.text();

                // Explicit check for routing failure returning app HTML
                const contentType = r.headers.get("Content-Type") || "";
                if (contentType.includes("text/html") || text.trim().startsWith("<!DOCTYPE") || text.trim().startsWith("<html")) {
                    throw new Error("API returned app HTML; /api route is not reaching Worker.");
                }

                // Edge Case: If worker returns JSON {ok:false} with 200
                if (text.startsWith('{"') && text.includes('"ok":false')) {
                    try {
                        const j = JSON.parse(text);
                        if (!j.ok) throw new Error(j.error || "API Error");
                    } catch (e) { /* ignore parse error if it's actually just text starting with {" */ }
                }

                validateBookText(text);

                // 3. Cache it (so next time we are offline-safe)
                await LibraryDB.saveBookAsset(currentBookId, text);

                processText(text);

            } catch (e: any) {
                console.error("Book Club Load Error", e);
                setBookClubError(e.message || "Failed to load");
                setVerseData(null);
            } finally {
                setIsBookClubLoading(false);
                setIsPaginating(false); // Stop spinner
            }
        };

        const processText = (text: string) => {
            try {
                validateBookText(text);
            } catch (err: any) {
                console.error(`[App] processText validation failed:`, err);
                setBookClubError(`Corrupted text detected: ${err.message}`);
                setVerseData(null);
                
                // Trigger purge
                LibraryDB.deleteBookAsset(currentBookId).catch(console.error);
                LibraryDB.getBook(currentBookId).then(async entry => {
                    if (entry) {
                        entry.textContent = '';
                        entry.chunkedChapters = [];
                        entry.status = 'metadata-only';
                        await LibraryDB.addBook(entry);
                        
                        // Reimport if numeric/Gutendex
                        const isNumeric = !isNaN(Number(currentBookId));
                        if (isNumeric) {
                            console.log(`[App] Triggering reimport for ${currentBookId}...`);
                            const recovered = await tryDownloadPublicDomain();
                            if (recovered) {
                                await activeCorpus.load();
                                const data = activeCorpus.getChapter(currentBookId, currentChapter);
                                if (data && Object.keys(data).length > 0) {
                                    setVerseData(data);
                                    setPageIndex(0);
                                    setIsPaginating(true);
                                    setBookClubError(null);
                                }
                            }
                        }
                    }
                }).catch(console.error);
                return;
            }

            // Simple chunking by double newline
            const paras = text.split(/\n\s*\n/);
            const vData: Record<string, string> = {};
            let vIndex = 1;
            paras.forEach((p) => {
                const clean = p.trim();
                if (clean) {
                    // Footnote filter regex:
                    if (/^\s*\[?(?:Footnote|Footnotes|Note|Notes)\s*\d+[:.\]\s]/i.test(clean)) {
                        return; // Skip footnote paragraph
                    }
                    vData[String(vIndex)] = clean;
                    vIndex++;
                }
            });

            setVerseData(vData);
            setPageIndex(0);
        };

        const tryDownloadPublicDomain = async () => {
            // Check if it is a Standard Ebooks book
            const entry = await LibraryDB.getBook(currentBookId);
            if (entry && entry.source === 'standard_ebooks') {
                try {
                    const epubUrl = entry.formats['application/epub+zip'] || entry.preferredFormatUrl;
                    if (!epubUrl) throw new Error("No EPUB download URL found.");
                    const buffer = await fetchArrayBufferViaWorker(epubUrl);
                    const result = await LocalImporter.processEpubData(new Uint8Array(buffer), entry.title);
                    await LibraryDB.saveBookAsset(currentBookId, result.fullText);
                    await LibraryDB.updateBookStatus(currentBookId, 'ready', result.fullText, result.chapters, {
                        source: 'standard_ebooks',
                        format: 'epub_parsed',
                        timestamp: Date.now()
                    });
                    return true;
                } catch (e: any) {
                    console.warn(`[Reader] Standard Ebooks download failed for ${currentBookId}`, e);
                    setBookClubError(e?.message || "Standard Ebooks download failed");
                    return false;
                }
            }

            // P0-G: Gutendex Fix - Check corpus OR if book is known Gutendex
            const isGutendexCorpus = activeCorpus.id === 'public_domain_gutendex';

            // Optimization: If not already in Gutendex mode, check if it SHOULD be
            let forceGutendex = false;
            if (!isGutendexCorpus) {
                // Check if it's a Gutendex book (numeric ID usually, or check metadata)
                if (entry && (entry.source === 'gutendex' || !isNaN(Number(entry.id)))) {
                    forceGutendex = true;
                }
            }

            if (!isGutendexCorpus && !forceGutendex) return false;

            const corpus: any = CorpusService.get('public_domain_gutendex');
            if (!corpus || typeof corpus.downloadBook !== 'function') return false;

            if (!entry) return false;
            try {
                await corpus.downloadBook(entry);
                await corpus.load();
                return true;
            } catch (e: any) {
                console.warn(`[Reader] Gutendex download failed for ${currentBookId}`, e);
                setBookClubError(e?.message || "Download failed");
                return false;
            }
        };

        const runLoadSequence = async () => {
            // Check validation and recover before loading
            const isValid = await verifyAndRecoverBook(currentBookId);
            if (!isValid) {
                console.warn(`[Reader] Book ${currentBookId} failed verification and is recovering.`);
                // If it is numeric, try to download and reload
                const isNumeric = !isNaN(Number(currentBookId));
                if (isNumeric) {
                    if (await tryDownloadPublicDomain()) {
                        await activeCorpus.load();
                        const dataAfter = activeCorpus.getChapter(currentBookId, currentChapter);
                        if (dataAfter && Object.keys(dataAfter).length > 0) {
                            console.log(`[Reader] Loaded after recovery download for ${currentBookId}`);
                            setVerseData(dataAfter);
                            setPageIndex(0);
                            setIsPaginating(true);
                            return;
                        }
                    }
                }
                // Do not blindly continue
                return;
            }
            
            if (bookClubItem) {
                await fetchBookClubText();
                return;
            }

            // For Gutendex/Public Domain, if recovery failed/cleared it, it will try to re-download in the next step
            await activeCorpus.load();
            
            // Route Gutendex/Public Domain IDs explicitly through PublicDomainCorpus and do NOT call /api/library/text/:id
            const entry = await LibraryDB.getBook(currentBookId);
            const isGutendexBook = entry && (entry.source === 'gutendex' || entry.source === 'standard_ebooks' || !isNaN(Number(entry.id)));
            const isNumeric = !isNaN(Number(currentBookId));
            
            if (isGutendexBook || isNumeric || activeCorpus.id === 'public_domain_gutendex') {
                const data = activeCorpus.getChapter(currentBookId, currentChapter);
                if (data && Object.keys(data).length > 0) {
                    console.log(`[Reader] Loaded local text for ${currentBookId}`);
                    setVerseData(data);
                    setPageIndex(0);
                    setIsPaginating(true);
                    return;
                }

                if (await tryDownloadPublicDomain()) {
                    const dataAfter = activeCorpus.getChapter(currentBookId, currentChapter);
                    if (dataAfter && Object.keys(dataAfter).length > 0) {
                        console.log(`[Reader] Loaded after download for ${currentBookId}`);
                        setVerseData(dataAfter);
                        setPageIndex(0);
                        setIsPaginating(true);
                        return;
                    }
                }
                
                // If it's a Gutendex book and download failed, stop here
                return;
            }

            // Fallback for custom files (not gutendex, not book club)
            console.warn(`[Reader] Fallback fetch for unknown book: ${currentBookId}`);
            await fetchBookClubText();
        };

        runLoadSequence();
    }, [currentBookId, currentChapter, activeCorpusId, view, audioManifest]);

    // Pagination & Restoration
    useEffect(() => {
        if (!verseData) {
            setPages([]);
            setIsPaginating(false);
            return;
        }

        const verses = Object.keys(verseData);
        // Heuristic: If empty (Cover?), make one page
        if (verses.length === 0) {
            setPages([[]]);
            setIsPaginating(false);
            return;
        }

        const newPages: string[][] = [];
        const CHUNK_SIZE = 4;
        for (let i = 0; i < verses.length; i += CHUNK_SIZE) {
            newPages.push(verses.slice(i, i + CHUNK_SIZE));
        }
        setPages(newPages);
        setIsPaginating(false);

        // Apply Restore
        if (pendingRestore) {
            if (pendingRestore.page < newPages.length) {
                setPageIndex(pendingRestore.page);
            }
            setPendingRestore(null);
        }
    }, [verseData, isDesktop]);

    // Persistence Effect
    useEffect(() => {
        if (!smartReader || !currentBookId || view !== 'reader' || !verseData) return;

        const save = () => {
            saveLastRead({
                corpusId: activeCorpusId,
                bookId: currentBookId,
                chapter: currentChapter,
                pageIndex: pageIndex,
                timestamp: Date.now()
            });
        };
        save();
    }, [currentBookId, currentChapter, pageIndex, smartReader, activeCorpusId]);

    // Haptics Helper
    const doHaptic = () => {
        if (haptics && !isSpeaking) triggerHaptic();
    };

    const handleNext = () => {
        doHaptic();
        if (isSpeaking) tts.stop('manual_page_next');
        advancePageOrChapter('manual');
    };

    const handlePrev = () => {
        doHaptic();
        lastManualNavAtRef.current = Date.now(); // Mark manual nav
        if (isSpeaking) tts.stop('manual_page_prev');
        if (pageIndex > 0) {
            setPageIndex(p => p - 1);
        } else if (currentChapter > 1) {
            setVerseData(null); // Invalidate for prev chapter too
            setCurrentChapter(c => c - 1);
        }
    };

    // Global Key Listener for Page Turns
    useEffect(() => {
        if (view !== 'reader') return;

        const handleKey = (e: KeyboardEvent) => {
            // Ignore if focus is in input
            const active = document.activeElement;
            if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || (active as HTMLElement).isContentEditable)) return;

            if (e.key === 'ArrowRight' || e.key === ' ') {
                e.preventDefault();
                handleNext();
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                handlePrev();
            }
        };

        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [view, pageIndex, currentChapter]); // Dependencies important for closure in handleNext? handleNext uses state.
    // Better: Helper functions inside effect or use updated refs?
    // handleNext uses 'readerStateRef' inside 'advancePageOrChapter'.
    // But 'handlePrev' uses state directly. 
    // To be safe, wrapped handlers use refs or specific deps.
    // 'handlePrev' uses pageIndex and currentChapter. So deps [pageIndex, currentChapter] are correct.

    // Stop speaking if unmounting or changing view
    useEffect(() => {
        return () => {
            if (isSpeaking) stopSpeaking();
        };
    }, []);

    const closeSettings = () => setShowSettings(null);

    const refreshLibrary = () => {
        fetch(getApiUrl('/library/manifest')).then(r => r.json()).then(d => {
            if (d.ok && d.items) {
                setAudioManifest(d.items);
                setToastMsg("Library refreshed");
            }
        }).catch(e => {
            console.error(e);
            setToastMsg("Refresh failed");
        });

        fetch(getApiUrl('/entitlement')).then(r => r.json()).then(d => {
            if (typeof d.isPro === 'boolean') setIsBookClubMember(d.isPro);
        }).catch(e => console.warn('Entitlement check failed (soft):', e));
    };

    const handlePlayAudio = (bookId: string, customAudio?: { url: string, title: string, coverUrl?: string }) => {
        if (customAudio) {
            setAudioOverlay({
                url: customAudio.url,
                title: customAudio.title,
                coverUrl: customAudio.coverUrl,
                bookId: bookId,
                isMember: true,
                upsellUrl: null
            });
            return;
        }

        const book = books.find(b => b.id === bookId);
        let bookAudio = audioManifest.find(m => m.id === bookId);

        // Legacy Siddhartha fallback
        if (!bookAudio && book?.title?.toLowerCase().includes('siddhartha')) {
            bookAudio = audioManifest.find(m => m.id === 'siddhartha');
        }

        if (bookAudio) {
            setAudioOverlay({
                url: bookAudio.audioUrl || bookAudio.playlistUrl || getApiUrl(`/library/playlist/${bookAudio.id}`),
                title: bookAudio.title,
                coverUrl: bookAudio.coverUrl,
                startTime: (bookAudio as any).trimStartSeconds,
                bookId: bookAudio.id,
                isMember: isMember,
                upsellUrl: 'https://corpora-content.b-cdn.net/upsell_v1.mp3' // Placeholder P0-D
            });
        } else {
            setToastMsg("Audio not found");
        }
    };

    const getFriendlyErrorMessage = (error: string | null): string => {
        if (!error) return "";
        const lower = error.toLowerCase();
        if (lower.includes("api returned app html") || lower.includes("<!doctype") || lower.includes("<html")) {
            return "This book is temporarily unavailable. Please check your internet connection and try reloading.";
        }
        if (lower.includes("failed to fetch") || lower.includes("networkerror") || lower.includes("network error")) {
            return "Network error. Please verify your internet connection and try again.";
        }
        return error;
    };

    return (
        <ErrorBoundary>
            <div className={`app-container ${theme}`} style={{ height: '100dvh', display: 'flex', flexDirection: 'column', background: 'var(--surface-color)', color: 'var(--text-color)' }}>
                {/* HEADER */}
                <header style={{
                    height: '50px', display: 'flex', alignItems: 'center', padding: '0 10px',
                    borderBottom: '1px solid var(--border-color)', justifyContent: 'space-between',
                    background: 'var(--surface-color)', zIndex: 10, position: 'relative'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', zIndex: 20 }}>
                        {/* Home Button (My Library) - Always show unless already there */}
                        {view !== 'library' && (
                            <button onClick={() => {
                                if (isSpeaking) tts.stop('manual_home_nav');
                                setView('library');
                            }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: '5px' }} title="My Library">
                                🏠
                            </button>
                        )}

                        {/* Discover Button - ALWAYS VISIBLE if not in Discover */}
                        <button onClick={() => setView('discover')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: '5px', display: 'flex', alignItems: 'center', gap: '5px' }} title="Search / Discover">
                            🔍 <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Discover</span>
                        </button>

                        {!view || view === 'library' || view === 'discover' ? (
                            <div style={{ fontWeight: 900, fontSize: '1.2rem', letterSpacing: '-0.5px', marginLeft: '10px' }}>ClaraNarratio</div>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {/* Chapter & Page Navigation */}
                                <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                    {(() => {
                                        const book = books.find(b => b.id === currentBookId);
                                        const maxCh = (book as any)?.chapterCount || 1;
                                        const noChapters = maxCh <= 1;

                                        return (
                                            <>
                                                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                                    {book ? book.title : currentBookId}
                                                </span>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    {!noChapters && (
                                                        <button
                                                            disabled={currentChapter <= 1}
                                                            onClick={() => {
                                                                lastManualNavAtRef.current = Date.now();
                                                                if (isSpeaking) tts.stop('manual_chapter_nav');
                                                                setCurrentChapter(c => Math.max(1, c - 1));
                                                            }}
                                                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', opacity: currentChapter <= 1 ? 0.3 : 1 }}
                                                        >
                                                            ◀
                                                        </button>
                                                    )}

                                                    <span
                                                        onClick={() => {
                                                            if (noChapters) return;
                                                            if (isSpeaking) tts.stop('manual_chapter_sheet');
                                                            setShowChapterSheet(true);
                                                        }}
                                                        style={{
                                                            fontSize: '0.8rem',
                                                            opacity: 0.8,
                                                            cursor: noChapters ? 'default' : 'pointer',
                                                            textDecoration: noChapters ? 'none' : 'underline',
                                                            textUnderlineOffset: '2px'
                                                        }}
                                                    >
                                                        {noChapters 
                                                            ? `Page ${pageIndex + 1} of ${pages.length || 1}` 
                                                            : `Chapter ${currentChapter} • Page ${pageIndex + 1} of ${pages.length || 1}`}
                                                    </span>

                                                    {!noChapters && (
                                                        <button
                                                            disabled={currentChapter >= maxCh}
                                                            onClick={() => {
                                                                lastManualNavAtRef.current = Date.now();
                                                                if (isSpeaking) tts.stop('manual_chapter_nav');
                                                                if (currentChapter < maxCh) {
                                                                    setCurrentChapter(c => c + 1);
                                                                }
                                                            }}
                                                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', opacity: currentChapter >= maxCh ? 0.3 : 1 }}
                                                        >
                                                            ▶
                                                        </button>
                                                    )}
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Floating Play Button (Southwest) - Always Visible in Reader */}
                    {view === 'reader' && (
                        <div style={{
                            position: 'fixed', bottom: '30px', left: '20px',
                            zIndex: 90, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();

                                    if (isSpeaking || isBuffering || tts.isPending || isPaginating) {
                                        tts.stop('stop_button');
                                        return;
                                    }

                                    // Play Logic
                                    // If it's a Book Club title, ALWAYS use the audio player overlay (Part A Requirement)
                                    // We check if audio exists for this book.
                                    const book = books.find(b => b.id === currentBookId);
                                    let bookAudio = audioManifest.find(m => m.id === currentBookId);
                                    if (!bookAudio && book?.title?.toLowerCase().includes('siddhartha')) {
                                        bookAudio = audioManifest.find(m => m.id === 'siddhartha');
                                    }

                                    if (bookAudio) {
                                        handlePlayAudio(currentBookId);
                                    } else {
                                        // Default TTS (Always fallback to strict TTS if not a Book Club title)
                                        const text = pages[pageIndex]?.map(k => verseData && verseData[k]).filter(Boolean) as string[] || [];
                                        if (text.length) {
                                            tts.play(text);
                                        } else {
                                            setToastMsg("No text to speak on this page");
                                        }
                                    }
                                }}
                                style={{
                                    width: '64px', height: '64px', borderRadius: '50%',
                                    background: 'var(--surface-color)',
                                    border: `3px solid ${isBuffering ? '#fa0' : ((isSpeaking || tts.isPending || isPaginating) ? '#4CAF50' : 'var(--border-color)')}`,
                                    color: 'var(--text-color)',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '1.6rem', padding: 0,
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    pointerEvents: 'auto', marginBottom: '4px',
                                    userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'none'
                                }}
                                aria-label={isSpeaking ? "Stop" : "Play"}
                            >
                                {(isSpeaking || isBuffering || tts.isPending || isPaginating) ?
                                    ((isBuffering || isPaginating) ? '⏳' : '■') :
                                    '▶'}
                            </button>
                            {!isSpeaking && resumeHint && (
                                <div style={{
                                    background: 'var(--surface-color)', padding: '2px 6px',
                                    borderRadius: '4px', fontSize: '0.65rem', color: 'var(--accent-color)',
                                    border: '1px solid var(--border-color)', pointerEvents: 'auto',
                                    maxWidth: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    position: 'absolute', bottom: '100%', marginBottom: '5px', left: '0'
                                }}>
                                    {resumeHint}
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '15px', zIndex: 20 }}>
                        {view === 'reader' && (
                            <div style={{ position: 'relative' }}>
                                <button onClick={() => setShowBookMenu(!showBookMenu)} style={{ background: 'transparent', border: 'none', fontSize: '1.2rem', cursor: 'pointer', padding: '0 5px' }}>⋮</button>
                                {showBookMenu && (
                                    <>
                                        <div style={{ position: 'fixed', inset: 0, zIndex: 90 }} onClick={() => setShowBookMenu(false)} />
                                        <div style={{
                                            position: 'absolute', top: '100%', right: 0,
                                            background: 'var(--surface-color)', border: '1px solid var(--border-color)',
                                            borderRadius: '8px', zIndex: 100, minWidth: '180px',
                                            boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                                            display: 'flex', flexDirection: 'column', overflow: 'hidden'
                                        }}>
                                            {(() => {
                                                // Optional: Other menu items or just empty if we removed everything
                                                // The user asked to remove "Play Narrated".
                                                return null;
                                            })()}


                                            <button onClick={() => {
                                                // Force reload remote
                                                setVerseData(null);
                                                setBookClubError(null);
                                                // We need to trigger the useEffect again or call fetch directly.
                                                // Toggling view is dirty but works, or we can expose the fetcher.
                                                // Simplest: just reload window for now? No, unmount/remount reader.
                                                setView('library');
                                                setTimeout(() => setView('reader'), 50);
                                            }} style={{ padding: '12px', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', color: 'var(--text-color)' }}>
                                                🔄 Reload Text (Force)
                                            </button>

                                            <button onClick={() => {
                                                clearLastRead(activeCorpusId, currentBookId);
                                                clearTtsProgress(activeCorpusId, currentBookId);
                                                setPendingRestore(null);
                                                setCurrentChapter(1);
                                                setPageIndex(0);
                                                setToastMsg("Book restarted");
                                                setShowBookMenu(false);
                                            }} style={{ padding: '12px', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', color: 'var(--text-color)' }}>
                                                ↺ Restart Book (Reset Position)
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {view === 'reader' && (
                            <button onClick={() => setShowChat(!showChat)} style={{ background: 'transparent', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>
                                {showChat ? '✖️' : '💬'}
                            </button>
                        )}

                        {view !== 'reader' && (
                            <button onClick={() => setView('help')} style={{ background: 'transparent', border: 'none', color: 'var(--text-color)', cursor: 'pointer', fontSize: '1.2rem', padding: '0 5px' }} title="Help">❓</button>
                        )}

                        {/* Import - Only on Library View, Gated by Sources */}
                        {view === 'library' && (() => {
                            const canImport = localStorage.getItem('source_pg_canada') === 'true' || localStorage.getItem('source_pg_australia') === 'true';
                            return (
                                <button
                                    onClick={() => {
                                        if (canImport) setShowImportModal(true);
                                        else alert("Enable a source in Settings → Sources to import.");
                                    }}
                                    style={{
                                        background: 'transparent', border: 'none',
                                        color: canImport ? 'var(--text-secondary)' : 'var(--text-disabled)',
                                        cursor: canImport ? 'pointer' : 'not-allowed',
                                        fontSize: '0.9rem', padding: '0 5px',
                                        opacity: canImport ? 1 : 0.5
                                    }}
                                    title={canImport ? "Import Book" : "Enable a source in Settings → Sources to import"}
                                >
                                    Import
                                </button>
                            );
                        })()}

                        <button onClick={() => setShowSettings('general')} style={{ background: 'transparent', border: 'none', color: 'var(--text-color)', cursor: 'pointer', fontSize: '1.2rem' }}>⚙️</button>
                    </div>
                </header>

                {/* VIEW CONTENT - Flex Layout for Desktop Chat */}
                <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
                    <main style={{ flex: 1, overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                        {bookClubError && view === 'reader' && (
                            <div style={{
                                position: 'absolute', inset: 0, zIndex: 100, background: 'var(--bg-color)',
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px',
                                padding: '20px', textAlign: 'center'
                            }}>
                                <h3>⚠️ Could not load book</h3>
                                <p style={{ color: 'var(--text-color)', opacity: 0.8, maxWidth: '80%', lineHeight: 1.5 }}>
                                    {getFriendlyErrorMessage(bookClubError)}
                                </p>
                                <div style={{ display: 'flex', gap: '15px' }}>
                                    <button onClick={() => {
                                        setBookClubError(null);
                                        setView('library');
                                        setTimeout(() => setView('reader'), 50);
                                    }} style={{
                                        padding: '10px 20px', fontSize: '1rem', borderRadius: '8px',
                                        background: 'var(--accent-color)', color: '#fff', border: 'none', cursor: 'pointer'
                                    }}>
                                        Retry
                                    </button>
                                    <button onClick={() => {
                                        setBookClubError(null);
                                        setView('library');
                                    }} style={{
                                        padding: '10px 20px', fontSize: '1rem', borderRadius: '8px',
                                        background: 'var(--surface-hover)', color: 'var(--text-color)',
                                        border: '1px solid var(--border-color)', cursor: 'pointer'
                                    }}>
                                        Back to Library
                                    </button>
                                </div>
                            </div>
                        )}

                        {view === 'library' && <LibraryView
                            activeCorpusId={activeCorpusId}
                            onOpenBook={handleSelectBook} // FIX: Was onSelectBook, which caused undefined prop in LibraryView
                            onDiscover={() => setView('discover')}
                            onHelp={() => setView('help')}
                            isPro={isMember}
                            audioManifest={audioManifest} // Keeping strictly to match existing potentially used "audioManifest" in unknown props
                            manifest={audioManifest} // Satisfy required 'manifest' prop
                        />}

                        {view === 'admin' && <AdminFactory />}
                        {view === 'admin-console' && <DeveloperConsole />}

                        {view === 'reader' && (
                            <ReaderFlipView
                                pages={pages}
                                verseData={verseData || {}}
                                onNext={handleNext}
                                onPrev={handlePrev}
                                isPaginating={isPaginating}
                                isSpeaking={isSpeaking}
                                onSpeakToggle={(text) => handleTtsToggle(text)}
                                pageIndex={pageIndex}
                                onGoToLibrary={() => setView('library')}
                            />
                        )}

                        {view === 'discover' && <DiscoverView
                            onGoToLibrary={() => setView('library')}
                            onOpenBook={handleSelectBook}
                            onPlayAudio={handlePlayAudio}
                            manifest={audioManifest || []} // Pass manifest explicitly as expected by DiscoverView
                            onToast={setToastMsg}
                            isMember={isMember}
                            isPro={isPro}
                            setIsPro={setIsPro}
                        />}

                        {view === 'help' && <HelpView />}
                    </main>

                    {/* Render Chat Drawer Properly */}
                    {showChat && (
                        <div style={isDesktop ? {
                            width: '350px',
                            borderLeft: '1px solid var(--border-color)',
                            background: 'var(--surface-color)',
                            zIndex: 20
                        } : {
                            position: 'fixed', top: '50px', bottom: 0, right: 0,
                            width: '100%',
                            zIndex: 150,
                            background: 'var(--surface-color)'
                        }}>
                            {/* @ts-ignore */}
                            <ChatDrawer
                                onClose={() => setShowChat(false)}
                                onOpenSettings={() => { setShowChat(false); setShowSettings('ai'); }}
                                currentVerses={verseData ? Object.entries(verseData).map(([k, v]) => ({ num: k, text: v })) : []}
                                currentRef={`${currentBookId} ${currentChapter}`}
                                onNavigate={handleJump}
                                messages={chatMessages}
                                onMessagesChange={setChatMessages}
                                studyMode={false}
                                onToggleStudyMode={() => { }}
                                corpusName={activeCorpus.title}
                                // Context Props
                                isPublicDomain={['public_domain_gutendex', 'siddhartha', 'douay_rheims'].includes(activeCorpus.id)}
                                contextText={pages && pages[pageIndex] ? pages[pageIndex].join('\n') : undefined}
                                bookTitle={books.find(b => b.id === currentBookId)?.title}
                                author={(books.find(b => b.id === currentBookId) as any)?.author}
                                chapterTitle={`Chapter ${currentChapter}`}
                                pageLabel={pages.length > 0 ? `${Math.floor((pageIndex + 1) / pages.length * 100)}%` : ''}
                            />
                        </div>
                    )}
                </div>

                {/* Toast */}
                {toastMsg && <Toast message={toastMsg} onClose={() => setToastMsg(null)} />}

                {/* PWA Update Prompt */}
                {needRefresh && (
                    <div style={{
                        position: 'fixed', bottom: 20, right: 20, zIndex: 3000,
                        background: 'var(--surface-color)', border: '1px solid var(--border-color)',
                        padding: '15px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        display: 'flex', gap: '10px', alignItems: 'center'
                    }}>
                        <span>New version available!</span>
                        <button onClick={() => updateServiceWorker(true)} style={{
                            background: 'var(--accent-color)', border: 'none', color: 'white',
                            padding: '5px 12px', borderRadius: '4px', cursor: 'pointer'
                        }}>Reload</button>
                    </div>
                )}

                {/* Settings Modal */}
                {showSettings && (
                    <SettingsModal
                        onClose={closeSettings}
                        initialTab={showSettings === 'debug' ? 'experimental' : showSettings as any}
                        readerMode={readerMode}
                        setReaderMode={setReaderMode}
                        haptics={haptics}
                        setHaptics={setHaptics}
                        onJump={handleJump}
                        onReindex={async () => {
                            const c = CorpusService.getActive();
                            if (c && 'reindex' in c) await (c as any).reindex();
                        }}
                        onRefreshLibrary={refreshLibrary}
                        onHome={() => setView('library')}
                        onToast={setToastMsg}
                        pwaProps={pwa}
                        debugInfo={{ view, currentBookId, chapter: currentChapter, activeCorpusId, textPreview: pages[pageIndex]?.join(' ').slice(0, 50) }}
                        wakeLock={wakeLock}
                        isMember={isMember}
                        onToggleMember={() => setDebugMemberOverride(!debugMemberOverride)}
                        isPro={isPro}
                        setIsPro={setIsPro}
                    />
                )}

                {/* Chapter Sheet Overlay */}
                {showChapterSheet && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.5)', zIndex: 2000,
                        display: 'flex', justifyContent: 'center', alignItems: 'flex-end'
                    }} onClick={() => setShowChapterSheet(false)}>
                        <div style={{
                            background: 'var(--surface-color)', width: '100%', maxWidth: '500px',
                            maxHeight: '60vh', borderTopLeftRadius: '16px', borderTopRightRadius: '16px',
                            display: 'flex', flexDirection: 'column', overflow: 'hidden',
                            boxShadow: '0 -4px 12px rgba(0,0,0,0.3)', marginBottom: isDesktop ? '20vh' : 0,
                            borderRadius: isDesktop ? '12px' : undefined
                        }} onClick={e => e.stopPropagation()}>
                            <div style={{ padding: '15px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontWeight: 600 }}>Select Chapter</span>
                                <button onClick={() => setShowChapterSheet(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
                            </div>
                            <div style={{ overflowY: 'auto', padding: '10px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))', gap: '10px' }}>
                                {(() => {
                                    const book = books.find(b => b.id === currentBookId);
                                    const count = (book as any)?.chapterCount || 1;
                                    return Array.from({ length: count }, (_, i) => i + 1).map(ch => (
                                        <button key={ch} onClick={() => {
                                            lastManualNavAtRef.current = Date.now();
                                            if (isSpeaking) tts.stop('manual_chapter_select');
                                            setCurrentChapter(ch);
                                            setShowChapterSheet(false);
                                        }} style={{
                                            padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)',
                                            background: currentChapter === ch ? 'var(--accent-color)' : 'var(--surface-color)',
                                            color: currentChapter === ch ? '#fff' : 'var(--text-color)',
                                            cursor: 'pointer'
                                        }}>
                                            {ch}
                                        </button>
                                    ));
                                })()}
                            </div>
                        </div>
                    </div>
                )}

                {/* Audio Overlay (Global) */}
                {audioOverlay && (
                    <AudioPlayerOverlay
                        url={audioOverlay.url}
                        title={audioOverlay.title}
                        coverUrl={audioOverlay.coverUrl}
                        startTime={(audioOverlay as any).startTime}
                        bookId={audioOverlay.bookId}
                        onClose={() => setAudioOverlay(null)}
                        isMember={(audioOverlay as any).isMember}
                        upsellUrl={(audioOverlay as any).upsellUrl}
                    />
                )}

                {showImportModal && (
                    <ImportModal
                        onClose={() => setShowImportModal(false)}
                        onBookImported={(id) => {
                            setShowImportModal(false);
                            handleSelectBook(id);
                        }}
                    />
                )}

                {toastMsg && <Toast message={toastMsg} onClose={() => setToastMsg(null)} />}
            </div>
        </ErrorBoundary>
    );
};

export default App;
